import { useTranslation } from 'react-i18next';
import { i18nDynamic } from 'shared/i18n';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useUser } from 'app/state/contexts';
import { useSaveStyle, useStyles } from 'entities/style';

import borderRadiusTopLeft from 'app/icons/border-radius-top-left.svg';
import borderRadiusTopRight from 'app/icons/border-radius-top-right.svg';
import borderRadiusBottomLeft from 'app/icons/border-radius-bottom-left.svg';
import borderRadiusBottomRight from 'app/icons/border-radius-bottom-right.svg';

import seperateSettings from 'app/icons/separate-settings.svg';
import spacingHorizontal from 'app/icons/spacing-horizontal.svg';
import spacingVertical from 'app/icons/spacing-vertical.svg';
import spacingBottom from 'app/icons/spacing-bottom.svg';
import spacingTop from 'app/icons/spacing-top.svg';
import spacingRight from 'app/icons/spacing-right.svg';
import spacingLeft from 'app/icons/spacing-left.svg';

import borderThickness from 'app/icons/border-thickness.svg';
import Dropdown from 'shared/forms/Dropdown';
import Tippy from '@tippyjs/react';

import { unWrapStyledParagraph, toggleConnect } from 'slate/extensions/styled-paragraph';
import { useSlate } from 'slate-react';

import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { ColorPicker } from 'app/components/editor/subtoolbar/ColorButton';
import cloneDeep from 'lodash.clonedeep';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useSetEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse } from 'app/state/contexts/EditorUiContext';
import type { StyledParagraphState } from 'slate/extensions/styled-paragraph/components/StyledParagraphElement';

type StyleDimension = { value?: number; unit?: string };
type NodeStyleValue = StyleDimension | string;
type NodeStyle = Record<string, NodeStyleValue>;
type StyleContent = Record<string, NodeStyle>;

const dimensionValue = (v: NodeStyleValue | undefined): number | undefined => (v && typeof v === 'object' ? v.value : undefined);
const stringValue = (v: NodeStyleValue | undefined): string | undefined => (typeof v === 'string' ? v : undefined);

export const StyledParagraph = () => {
  const { t } = useTranslation('styledparagraph');

  const { data: pDocument } = useEditorDocument();
  const activeSection = useEditorCoarse().activeSection;
  const isDirty = useEditorCoarse().isDirty;

  const editor = useSlate();
  const graphicalStyleId = pDocument.content.design.find((d) => d?.type === 'graphical-variables')?.styleId;
  const pageStyleId = pDocument.content.design.find((d) => d.type === 'page-variables' && d.pageId === activeSection.uuid)?.styleId;
  const { selectedOrganization } = useUser();
  const { shadowHost } = useEditorContext();
  const { styledParagraph, setStyledParagraph } = useSidebar();
  const styledParagraphNode = styledParagraph as StyledParagraphState;
  const setEditorDocument = useSetEditorDocument();
  const { data: stylesData = [] } = useStyles({
    organization: selectedOrganization,
    documentRevisionId: pDocument.revisionId,
    styleIds: [graphicalStyleId, pageStyleId],
  });
  const [graphicalStyle, pageStyle] = stylesData;

  const savedStyle = pageStyle?.content as StyleContent | undefined;

  const { mutate: saveStyle } = useSaveStyle({
    organization: selectedOrganization,
    documentSuperId: pDocument.superId,
    pageId: activeSection.uuid,
    templateSuperId: activeSection.templateSuperId,
  });
  const [style, setStyle] = useState<StyleContent | undefined>(savedStyle);
  const latestStyleRef = useRef<StyleContent | undefined>(style);
  const nodeStyle = style?.[`xrp-super-id-${styledParagraphNode?.id}`];
  const activePageElement = shadowHost?.shadowRoot?.querySelector(`#id-${activeSection.uuid}`);
  const paragraphElement = (activePageElement ?? shadowHost?.shadowRoot)?.querySelector<HTMLElement>(`.xrp-super-id-${styledParagraphNode?.id}`);
  const pageContentElement = paragraphElement?.closest('.page-content');
  const paragraphDimensions = paragraphElement?.getBoundingClientRect();
  const pageDimensions = pageContentElement?.getBoundingClientRect();
  const paperWidth = Math.floor(pageDimensions?.width ?? 794);
  const paperHeight = Math.floor(pageDimensions?.height ?? 1123);
  const getNumericValue = (key: string) => {
    const styleValue = dimensionValue(nodeStyle?.[key]);
    if (styleValue !== undefined && typeof styleValue === 'number' && !isNaN(styleValue)) return Math.round(styleValue);
    const relValue = relativeParagraphDimension?.[key];
    if (relValue !== undefined && typeof relValue === 'number' && !isNaN(relValue)) return Math.round(relValue);
    return 0;
  };
  const getMargin = (side: string) => Math.round(dimensionValue(nodeStyle?.[`margin-${side}`]) ?? 0);
  const outerWidth = () => getNumericValue('width') + getMargin('left') + getMargin('right');
  const outerHeight = () => getNumericValue('height') + getMargin('top') + getMargin('bottom');
  const relativeParagraphDimension: Record<string, number> | undefined = useMemo(
    () =>
      paragraphDimensions && pageDimensions
        ? {
            top: paragraphDimensions.top - pageDimensions.top,
            left: paragraphDimensions.left - pageDimensions.left,
            width: paragraphDimensions.width,
            height: paragraphDimensions.height,
          }
        : undefined,
    [pageDimensions, paragraphDimensions]
  );

  useEffect(() => {
    if (savedStyle) {
      setStyle(savedStyle);
    }
  }, [savedStyle]);
  useEffect(() => {
    latestStyleRef.current = style;
  }, [style]);
  const colors = useMemo(() => {
    const content = graphicalStyle?.content as Record<string, string> | undefined;
    return Object.keys(COLOR_MAP).map((key) => {
      return {
        name: key,
        title: i18nDynamic((COLOR_MAP as Record<string, { label: string }>)[key].label),
        value: content?.[key],
        icon: <div style={{ height: '10px', width: '10px', backgroundColor: content?.[key] }} />,
      };
    });
  }, [graphicalStyle?.content]);
  const backgroundColorExists = useMemo(() => colors.some((color) => color.value === nodeStyle?.['background-color']), [colors, nodeStyle]);
  const borderColorExists = useMemo(() => colors.some((color) => color.value === nodeStyle?.['border-color']), [colors, nodeStyle]);
  const resetValues = (keys?: string[]) => {
    const classKey = `xrp-super-id-${styledParagraphNode.id}`;
    const newStyle = { ...style };
    if (!newStyle[classKey]) {
      newStyle[classKey] = {};
    }
    if (keys && keys.length > 0) {
      keys.forEach((key) => {
        delete newStyle[classKey][key];
      });
    } else {
      delete newStyle[classKey];
    }
    if (newStyle[classKey] && Object.keys(newStyle[classKey]).length === 0) {
      delete newStyle[classKey];
    }

    paragraphElement?.removeAttribute('style');
    setStyle(newStyle);
    save();
  };

  const removeStyledParagraph = () => {
    const classKey = `xrp-super-id-${styledParagraphNode.id}`;
    const newStyle = { ...style };
    delete newStyle[classKey];
    paragraphElement?.removeAttribute('style');
    setStyle(newStyle);
    latestStyleRef.current = newStyle;
    saveSettings();
    unWrapStyledParagraph(editor, styledParagraphNode?.id);
  };

  const handleChange = (newProperties: Record<string, unknown>) => {
    const newStyle: StyleContent = cloneDeep(style) ?? {};
    const classKey = `xrp-super-id-${styledParagraphNode.id}`;
    if (!newStyle[classKey]) {
      newStyle[classKey] = {};
    }
    try {
      Object.entries(newProperties).forEach(([propertyKey, propertyValue]) => {
        const allowedStringInputs = ['background-color', 'border-color', 'border-style', 'position'];
        const isStringInput = allowedStringInputs.includes(propertyKey);

        if (
          propertyValue === undefined ||
          propertyValue === null ||
          propertyValue === '' ||
          propertyValue === 'mixe' ||
          (!isStringInput && isNaN(parseInt(propertyValue as string)))
        ) {
          delete newStyle[classKey][propertyKey];
        } else {
          if (isStringInput) {
            newStyle[classKey][propertyKey] = propertyValue as string;
          } else {
            newStyle[classKey][propertyKey] = { value: parseFloat(propertyValue as string), unit: 'px' };
          }
        }
      });

      if (!paragraphElement) return;
      paragraphElement.removeAttribute('style');
      const classProperties = newStyle[classKey];
      if (Object.keys(classProperties).length === 0) {
        delete newStyle[classKey];
      }
      setStyle(newStyle);
      const cssProperties: Record<string, string> = {};
      Object.entries(classProperties).forEach(([key, value]) => {
        if (typeof value === 'object' && value.value !== undefined && value.unit !== undefined) {
          cssProperties[key] = `${value.value}${value.unit}`;
        } else if (typeof value === 'string') {
          cssProperties[key] = value;
        }
      });
      Object.entries(cssProperties).forEach(([key, value]) => {
        paragraphElement.style.setProperty(key, value);
      });
      save();
    } catch (e) {
      cancelSave();
      console.error('Error while updating style', styledParagraphNode?.id, newProperties, e);
    }
  };

  const saveSettings = () => {
    saveStyle(latestStyleRef.current, {
      onSuccess: (doc) => {
        setEditorDocument(doc);
      },
    });
  };
  const [save, cancelSave] = useDelayedSave(saveSettings, 1500);

  const formatPlaceholder = useCallback(
    (key: string) => {
      let values: string[] = [];
      if (!nodeStyle) return;
      const filterStyles = (prefix: string, suffixes: string[]) =>
        Object.entries(nodeStyle)
          .filter(([k]) => k.startsWith(prefix) && suffixes.some((suffix) => k.endsWith(suffix)))
          .map(([, v]) => `${dimensionValue(v)}`);

      switch (key) {
        case 'border-radius':
          values = filterStyles('border', ['radius']);
          break;
        case 'margin-horizontal':
          values = filterStyles('margin', ['left', 'right']);
          break;
        case 'margin-vertical':
          values = filterStyles('margin', ['top', 'bottom']);
          break;
        case 'padding-horizontal':
          values = filterStyles('padding', ['left', 'right']);
          break;
        case 'padding-vertical':
          values = filterStyles('padding', ['top', 'bottom']);
          break;
        case 'border':
          values = filterStyles('border', ['width']);
          break;
        default:
          values = [];
          break;
      }

      if (values.length === 0) return '';
      const uniqueValues = new Set(values);
      return uniqueValues.size === 1 ? `${values[0]}` : 'mixed';
    },
    [nodeStyle]
  );

  const formatNumberPlaceholder = useCallback(
    (key: string) => {
      const styleValue = dimensionValue(nodeStyle?.[key]);
      const relValue = relativeParagraphDimension?.[key];

      if (styleValue !== undefined && typeof styleValue === 'number' && !isNaN(styleValue)) {
        return Math.round(styleValue);
      }
      if (relValue !== undefined && typeof relValue === 'number' && !isNaN(relValue)) {
        return Math.round(relValue);
      }
      return 0;
    },
    [nodeStyle, relativeParagraphDimension]
  );

  const handleRelease = () => {
    const changes: Record<string, unknown> = { position: 'absolute' };
    const pendingPos = styledParagraphNode?.pendingPosition;
    const pendingDims = styledParagraphNode?.pendingDimensions;

    if (pendingPos) {
      changes.top = pendingPos.top;
      changes.left = pendingPos.left;
    } else if (dimensionValue(nodeStyle?.['top']) === undefined) {
      changes.top = 0;
    }
    if (!pendingPos && dimensionValue(nodeStyle?.['left']) === undefined) {
      changes.left = 0;
    }

    if (pendingDims) {
      changes.width = pendingDims.width;
      changes.height = pendingDims.height;
    } else {
      if (dimensionValue(nodeStyle?.['width']) === undefined) {
        changes.width = formatNumberPlaceholder('width');
      }
      if (dimensionValue(nodeStyle?.['height']) === undefined) {
        changes.height = formatNumberPlaceholder('height');
      }
    }

    handleChange(changes);
    setStyledParagraph((prev: StyledParagraphState | null) => ({
      ...prev,
      connected: !prev?.connected,
      pendingPosition: undefined, // Clear pending position after saving
      pendingDimensions: undefined,
    }));
    if (styledParagraphNode.id) {
      toggleConnect(editor, styledParagraphNode.id, nodeStyle);
    }
  };
  const handleAttach = () => {
    const changes: Record<string, unknown> = {};
    const pendingPos = styledParagraphNode?.pendingPosition;
    const pendingDims = styledParagraphNode?.pendingDimensions;
    // If user has dragged (pending position exists), use those values
    if (pendingPos) {
      changes.top = pendingPos.top;
      changes.left = pendingPos.left;
    } else {
      if (dimensionValue(nodeStyle?.['top']) === undefined) {
        changes.top = relativeParagraphDimension?.top;
      }
      if (dimensionValue(nodeStyle?.['left']) === undefined) {
        changes.left = relativeParagraphDimension?.left;
      }
    }

    if (pendingDims) {
      changes.width = pendingDims.width;
      changes.height = pendingDims.height;
    }
    if (Object.keys(changes).length > 0) {
      handleChange(changes);
    }
    setStyledParagraph((prev: StyledParagraphState | null) => ({
      ...prev,
      connected: !prev?.connected,
      pendingPosition: undefined, // Clear pending position after saving
      pendingDimensions: undefined,
    }));
    if (styledParagraphNode.id) {
      toggleConnect(editor, styledParagraphNode.id, nodeStyle);
    }
  };

  if (!styledParagraphNode)
    return (
      <div className="preview--container" style={{ height: '100%' }}>
        <div className="px-3 pt-4 pb-3 w-100 bg-white sticky-top">
          <div className="fw-bold">{t('sidebar.title')}</div>
        </div>
        <div className="styled-paragraph-wrapper px-3 no-paragraph">
          <p>{t('sidebar.no-paragraph')}</p>
        </div>
      </div>
    );

  return (
    <div className="preview--container h-100">
      <div className="styled-paragraf-header ">
        <div className="fw-bold">{t('sidebar.title')}</div>
        <div className="styled-paragraph-header-buttons">
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('reset-tooltip')}</p>} placement="bottom">
            <button className="not-a-button" onClick={() => resetValues()}>
              <i className="fa-regular fa-trash"></i>
            </button>
          </Tippy>
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('remove-tooltip')}</p>} placement="bottom">
            <button className="not-a-button" onClick={removeStyledParagraph}>
              <i className="fa-regular fa-x"></i>
            </button>
          </Tippy>
        </div>
      </div>
      <div className="styled-paragraph-wrapper px-3" style={{ height: '100%' }}>
        <div className="styled-paragraph-setting-wrapper">
          <p>{t('sidebar.border-radius')}</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <img src={borderRadiusTopLeft} alt="Border radius icon" />
            <div style={{ gap: '2px', display: 'flex' }}>
              <input
                placeholder={formatPlaceholder('border-radius')}
                type="text"
                pattern="[0-9]*"
                value={formatPlaceholder('border-radius') ?? ''}
                onChange={(e) => {
                  handleChange({
                    'border-top-left-radius': e.target.value,
                    'border-top-right-radius': e.target.value,
                    'border-bottom-left-radius': e.target.value,
                    'border-bottom-right-radius': e.target.value,
                  });
                }}
              />
              <p>px</p>
            </div>
            <Tippy
              content={<SettingsPopOver type="border-radius" values={nodeStyle} onChange={handleChange} />}
              placement="left"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <img src={seperateSettings} alt="Seperate Settings Icon" className="settings-icon" />
            </Tippy>
          </div>
        </div>

        <div className="styled-paragraph-setting-wrapper">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p>{t('sidebar.margin-title')}</p>
            <p>{t('sidebar.margin-description')}</p>
          </div>
          <div style={{ gap: '8px' }} className="styled-paragraph-setting-wrapper">
            <div className="styled-paragraph-setting-inner">
              <div className="styled-paragraph-input-wrapper">
                <img src={spacingHorizontal} alt="Spacing Horizontal Icon" />
                <div style={{ gap: '2px', display: 'flex' }}>
                  <input
                    placeholder={formatPlaceholder('margin-horizontal')}
                    type="text"
                    pattern="[0-9]*"
                    value={formatPlaceholder('margin-horizontal') ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange({ 'margin-right': value, 'margin-left': value });
                    }}
                  />
                  <p>px</p>
                </div>
              </div>
              <div className="styled-paragraph-input-wrapper">
                <img src={spacingVertical} alt="Spacing Vertical Icon" />
                <div style={{ gap: '2px', display: 'flex' }}>
                  <input
                    placeholder={formatPlaceholder('margin-vertical')}
                    type="text"
                    pattern="[0-9]*"
                    value={formatPlaceholder('margin-vertical') ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange({
                        'margin-top': value,
                        'margin-bottom': value,
                      });
                    }}
                  />
                  <p>px</p>
                </div>
              </div>
            </div>
            <Tippy
              content={<SettingsPopOver type="margin" values={nodeStyle} onChange={handleChange} />}
              placement="left"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <img src={seperateSettings} alt="Seperate Settings Icon" className="settings-icon" />
            </Tippy>
          </div>
        </div>
        <div className="styled-paragraph-setting-wrapper">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p>{t('sidebar.padding-title')}</p>
            <p>{t('sidebar.padding-description')}</p>
          </div>
          <div style={{ gap: '8px' }} className="styled-paragraph-setting-wrapper">
            <div className="styled-paragraph-setting-inner">
              <div className="styled-paragraph-input-wrapper">
                <img src={spacingHorizontal} alt="Spacing Horizontal Icon" />
                <div style={{ gap: '2px', display: 'flex' }}>
                  <input
                    placeholder={formatPlaceholder('padding-horizontal')}
                    type="text"
                    pattern="[0-9]*"
                    value={formatPlaceholder('padding-horizontal') ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange({
                        'padding-left': value,
                        'padding-right': value,
                      });
                    }}
                  />
                  <p>px</p>
                </div>
              </div>
              <div className="styled-paragraph-input-wrapper">
                <img src={spacingVertical} alt="Spacing Vertical Icon" />
                <div style={{ gap: '2px', display: 'flex' }}>
                  <input
                    placeholder={formatPlaceholder('padding-vertical')}
                    type="text"
                    pattern="[0-9]*"
                    value={formatPlaceholder('padding-vertical') ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleChange({
                        'padding-top': value,
                        'padding-bottom': value,
                      });
                    }}
                  />
                  <p>px</p>
                </div>
              </div>
            </div>
            <Tippy
              content={<SettingsPopOver type="padding" values={nodeStyle} onChange={handleChange} />}
              placement="left"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <img src={seperateSettings} alt="Seperate Settings Icon" className="settings-icon" />
            </Tippy>
          </div>
        </div>
        <div className="divider horizontal bg-blue-500" />
        <div>
          <p className="mb-r-1">{t('sidebar.background-color')}</p>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
            <Dropdown
              items={colors}
              sm
              value={backgroundColorExists ? nodeStyle?.['background-color'] : null}
              placeHolder={backgroundColorExists ? '' : t('custom-color')}
              onChange={(e) => {
                handleChange({ 'background-color': e });
              }}
              key="background-color-dropdown"
            />
            <Tippy
              content={
                <ColorPicker
                  defaultValue={stringValue(nodeStyle?.['background-color'])}
                  handleChangeCallBack={(color) => {
                    handleChange({ 'background-color': color });
                  }}
                  key="background-color-picker"
                />
              }
              placement="left-start"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <div className="styled-paragraph-color-icon" style={{ background: stringValue(nodeStyle?.['background-color']) ?? 'transparent' }} />
            </Tippy>
          </div>
        </div>
        <div className="divider horizontal bg-blue-500" />
        <div>
          <p className="mb-r-1">{t('sidebar-border')}</p>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
            <Dropdown
              items={colors}
              sm
              value={borderColorExists ? nodeStyle?.['border-color'] : null}
              placeHolder={borderColorExists ? '' : t('custom-color')}
              onChange={(e) => handleChange({ 'border-color': e, 'border-style': 'solid' })}
              key="border-color-dropdown"
            />
            <Tippy
              content={
                <ColorPicker
                  defaultValue={stringValue(nodeStyle?.['border-color'])}
                  handleChangeCallBack={(color) => {
                    if (!color) {
                      resetValues(['border-color', 'border-style', 'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width']);
                      return;
                    }
                    handleChange({ 'border-color': color, 'border-style': 'solid' });
                  }}
                  key="border-color-picker"
                />
              }
              placement="left-start"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <div className="styled-paragraph-color-icon" style={{ background: stringValue(nodeStyle?.['border-color']) ?? '#0000' }} />
            </Tippy>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', placeSelf: 'end', marginTop: '16px' }}>
            <img src={borderThickness} alt="Border Thickness Icon" />
            <div style={{ gap: '2px', display: 'flex', marginRight: '8px' }}>
              <input
                placeholder={formatPlaceholder('border')}
                type="text"
                pattern="[0-9]*"
                style={{ marginLeft: '16px' }}
                value={formatPlaceholder('border') ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange({
                    'border-top-width': value,
                    'border-right-width': value,
                    'border-bottom-width': value,
                    'border-left-width': value,
                    'border-style': value ? 'solid' : 'none',
                  });
                }}
              />
              <p>px</p>
            </div>
            <Tippy
              content={
                <SettingsPopOver
                  type="border"
                  values={nodeStyle}
                  onChange={(value) =>
                    handleChange({
                      'border-width': 0,
                      'border-style': value ? 'solid' : 'none',
                      ...value,
                    })
                  }
                />
              }
              placement="left"
              theme="light"
              interactive={true}
              trigger="click"
              appendTo="parent"
            >
              <img src={seperateSettings} alt="Seperate Settings Icon" className="settings-icon" />
            </Tippy>
          </div>
        </div>
        <div className="divider horizontal bg-blue-500" />
        <div className="mb-r-1 d-flex flex-row justify-content-between align-items-center">
          <p>{t('position-title')}</p>
          <Tippy content={<p className="gaby-tooltip arrow-top">{t('reset-position-tooltip')}</p>} placement="bottom">
            <button
              className="not-a-button"
              onClick={() => {
                resetValues(['position', 'top', 'left', 'width', 'height']);
              }}
            >
              <i className="fa-regular fa-trash"></i>
            </button>
          </Tippy>
        </div>
        <div className="styled-paragraph-setting-wrapper">
          <p>{t('position-top')}</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ gap: '2px', display: 'flex' }}>
              <input
                placeholder={String(formatNumberPlaceholder('top'))}
                type="text"
                pattern="[0-9]*"
                value={formatNumberPlaceholder('top')}
                onChange={(e) => {
                  if (!/^$|^[0-9]+$/.test(e.target.value)) return;
                  handleChange({
                    position: 'absolute',
                    top: Math.min(Math.round(Number(e.target.value)), paperHeight - outerHeight()),
                  });
                }}
              />
              <p>px</p>
            </div>
          </div>
        </div>
        <div className="styled-paragraph-setting-wrapper">
          <p>{t('position-left')}</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ gap: '2px', display: 'flex' }}>
              <input
                placeholder={String(formatNumberPlaceholder('left'))}
                type="text"
                pattern="[0-9]*"
                value={formatNumberPlaceholder('left')}
                onChange={(e) => {
                  if (!/^$|^[0-9]+$/.test(e.target.value)) return;
                  handleChange({
                    position: 'absolute',
                    left: Math.min(Math.round(Number(e.target.value)), paperWidth - outerWidth()),
                  });
                }}
              />
              <p>px</p>
            </div>
          </div>
        </div>
        <div className="styled-paragraph-setting-wrapper">
          <p>{t('position-width')}</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ gap: '2px', display: 'flex' }}>
              <input
                placeholder={String(formatNumberPlaceholder('width'))}
                type="text"
                pattern="[0-9]*"
                value={formatNumberPlaceholder('width')}
                onChange={(e) => {
                  if (!/^$|^[0-9]+$/.test(e.target.value)) return;
                  const widthValue = Math.min(
                    Math.round(Number(e.target.value)),
                    paperWidth - getNumericValue('left') - getMargin('left') - getMargin('right')
                  );
                  const heightValue = formatNumberPlaceholder('height');
                  handleChange({
                    width: widthValue,
                    ...(nodeStyle?.['border-color'] && { 'border-style': widthValue === 0 && heightValue === 0 ? 'none' : 'solid' }),
                  });
                }}
              />
              <p>px</p>
            </div>
          </div>
        </div>
        <div className="styled-paragraph-setting-wrapper">
          <p>{t('position-height')}</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ gap: '2px', display: 'flex' }}>
              <input
                placeholder={String(formatNumberPlaceholder('height'))}
                type="text"
                pattern="[0-9]*"
                value={formatNumberPlaceholder('height')}
                onChange={(e) => {
                  if (!/^$|^[0-9]+$/.test(e.target.value)) return;
                  const heightValue = Math.min(
                    Math.round(Number(e.target.value)),
                    paperHeight - getNumericValue('top') - getMargin('top') - getMargin('bottom')
                  );
                  const widthValue = formatNumberPlaceholder('width');
                  handleChange({
                    height: heightValue,
                    ...(nodeStyle?.['border-color'] && { 'border-style': heightValue === 0 && widthValue === 0 ? 'none' : 'solid' }),
                  });
                }}
              />
              <p>px</p>
            </div>
          </div>
        </div>

        <button onClick={() => (styledParagraphNode.connected ? handleRelease() : handleAttach())} className="btn btn-secondary" disabled={isDirty}>
          {isDirty ? (
            'Sparar ...'
          ) : (
            <span className="d-flex flex-row gap-2 justify-content-center align-items-center">
              {styledParagraphNode.connected ? t('element-release') : t('element-attach')}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

interface SettingsPopOverProps {
  type: string;
  onChange: (value: Record<string, unknown>) => void;
  values?: NodeStyle;
}

const SettingsPopOver = ({ type, onChange, values }: SettingsPopOverProps) => {
  const settingsType = type === 'border-radius' ? 'radius' : 'spacing';

  const formatKey = (type: string, key: string) => {
    let cssProperty: string | undefined;
    switch (type) {
      case 'margin':
      case 'padding':
        cssProperty = `${type}-${key}`;
        break;
      case 'border':
        cssProperty = `border-${key}-width`;
        break;
      case 'border-radius':
        cssProperty = `border-${key}-radius`;
        break;
      default:
        break;
    }

    return cssProperty ? dimensionValue(values?.[cssProperty]) ?? '' : '';
  };

  const handleSettingsChange = (value: string, key: string) => {
    let cssProperty: Record<string, string> | undefined;
    switch (type) {
      case 'margin':
      case 'padding':
        cssProperty = { [`${type}-${key}`]: value };
        break;
      case 'border':
        cssProperty = { [`border-${key}-width`]: value };
        break;
      case 'border-radius':
        cssProperty = { [`border-${key}-radius`]: value };
        break;
      default:
        break;
    }
    if (cssProperty) onChange(cssProperty);
  };

  return (
    <div className="seperate-setting-wrapper">
      <div className="seperate-setting-inner">
        <div>
          <img src={settingsType === 'spacing' ? spacingTop : borderRadiusTopLeft} alt="Spacing Top Icon" />
          <div style={{ gap: '2px', display: 'flex' }}>
            <input
              placeholder="0"
              type="text"
              pattern="[0-9]*"
              value={formatKey(type, settingsType === 'spacing' ? 'top' : 'top-left')}
              onChange={(e) => handleSettingsChange(e.target.value, settingsType === 'spacing' ? 'top' : 'top-left')}
            />
            <p>px</p>
          </div>
        </div>
        <div>
          <img src={settingsType === 'spacing' ? spacingRight : borderRadiusTopRight} alt="Spacing Right Icon" />
          <div style={{ gap: '2px', display: 'flex' }}>
            <input
              placeholder="0"
              type="text"
              pattern="[0-9]*"
              value={formatKey(type, settingsType === 'spacing' ? 'right' : 'top-right')}
              onChange={(e) => handleSettingsChange(e.target.value, settingsType === 'spacing' ? 'right' : 'top-right')}
            />
            <p>px</p>
          </div>
        </div>
      </div>
      <div className="seperate-setting-inner">
        <div>
          <img src={settingsType === 'spacing' ? spacingBottom : borderRadiusBottomLeft} alt="Spacing Top Icon" />
          <div style={{ gap: '2px', display: 'flex' }}>
            <input
              placeholder="0"
              type="text"
              pattern="[0-9]*"
              value={formatKey(type, settingsType === 'spacing' ? 'bottom' : 'bottom-left')}
              onChange={(e) => handleSettingsChange(e.target.value, settingsType === 'spacing' ? 'bottom' : 'bottom-left')}
            />
            <p>px</p>
          </div>
        </div>
        <div>
          <img src={settingsType === 'spacing' ? spacingLeft : borderRadiusBottomRight} alt="Spacing Right Icon" />
          <div style={{ gap: '2px', display: 'flex' }}>
            <input
              placeholder="0"
              type="text"
              pattern="[0-9]*"
              value={formatKey(type, settingsType === 'spacing' ? 'left' : 'bottom-right')}
              onChange={(e) => handleSettingsChange(e.target.value, settingsType === 'spacing' ? 'left' : 'bottom-right')}
            />
            <p>px</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const COLOR_MAP = {
  xrpPrimaryColor: {
    label: 'graphical-variable:xrpPrimaryColor',
  },
  xrpSecondaryColor: {
    label: 'graphical-variable:xrpSecondaryColor',
  },
  xrpTertiaryColor: {
    label: 'graphical-variable:xrpTertiaryColor',
  },
  xrpQuaternaryColor: {
    label: 'graphical-variable:xrpQuaternaryColor',
  },
  xrpHeadingColor: {
    label: 'graphical-variable:xrpHeadingColor',
  },
};
